<template>
  <div class="admin-page">
    <h2>Комментарии</h2>

    <div class="admin-filters">
      <label class="admin-filter">
        <span>Тип</span>
        <select v-model="typeFilter" class="admin-select" @change="loadComments">
          <option value="">Все</option>
          <option value="track">Треки</option>
          <option value="trip">Поездки</option>
          <option value="event">События</option>
        </select>
      </label>
    </div>

    <p v-if="error" class="error-message">{{ error }}</p>

    <div class="panel">
      <p v-if="loading" class="empty-text">Загрузка...</p>
      <p v-else-if="comments.length === 0" class="empty-text">Комментариев нет.</p>

      <div v-else class="admin-table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Тип</th>
              <th>Автор</th>
              <th>Текст</th>
              <th>Скрыт</th>
              <th>Создан</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="comment in comments" :key="`${comment.comment_type}-${comment.id}`">
              <td data-label="Тип">{{ commentTypeLabel(comment.comment_type) }}</td>
              <td data-label="Автор">
                <UserIdentity :user="comment.user" size="sm" />
              </td>
              <td data-label="Текст" class="admin-comment-body">{{ truncateText(comment.body) }}</td>
              <td data-label="Скрыт">
                <span v-if="comment.content_hidden">
                  Да, {{ formatDateTime(comment.content_hidden_at) }}
                </span>
                <span v-else>Нет</span>
              </td>
              <td data-label="Создан">{{ formatDateTime(comment.created_at) }}</td>
              <td data-label="Действия">
                <div class="btn-row admin-actions">
                  <button
                    v-if="!comment.content_hidden"
                    type="button"
                    class="btn btn-secondary btn-sm"
                    :disabled="busyKey === commentKey(comment)"
                    @click="hideContent(comment)"
                  >
                    Скрыть содержимое
                  </button>
                  <button
                    type="button"
                    class="btn btn-danger btn-sm"
                    :disabled="busyKey === commentKey(comment)"
                    @click="removeComment(comment)"
                  >
                    Удалить
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import UserIdentity from '../../components/UserIdentity.vue'
import { destroyAdminComment, fetchAdminComments, hideAdminComment } from '../../api/admin'
import { extractApiError } from '../../utils/apiError'
import { formatDateTime, truncateText } from '../../utils/formatDateTime'
import { commentTypeLabel } from '../../utils/moderationLabels'

const comments = ref([])
const loading = ref(true)
const error = ref('')
const busyKey = ref(null)
const typeFilter = ref('')

function commentKey(comment) {
  return `${comment.comment_type}-${comment.id}`
}

async function loadComments() {
  loading.value = true
  error.value = ''
  try {
    comments.value = await fetchAdminComments({ type: typeFilter.value || undefined })
  } catch (err) {
    error.value = extractApiError(err, 'Не удалось загрузить комментарии')
  } finally {
    loading.value = false
  }
}

function updateComment(updated) {
  comments.value = comments.value.map((item) =>
    item.id === updated.id && item.comment_type === updated.comment_type ? updated : item
  )
}

async function runAction(comment, action) {
  busyKey.value = commentKey(comment)
  error.value = ''
  try {
    await action()
  } catch (err) {
    error.value = extractApiError(err)
  } finally {
    busyKey.value = null
  }
}

async function hideContent(comment) {
  if (!window.confirm('Скрыть содержимое этого комментария?')) return
  await runAction(comment, async () => {
    const updated = await hideAdminComment(comment.comment_type, comment.id)
    updateComment(updated)
  })
}

async function removeComment(comment) {
  if (!window.confirm('Удалить этот комментарий? Это необратимо.')) return
  await runAction(comment, async () => {
    await destroyAdminComment(comment.comment_type, comment.id)
    comments.value = comments.value.filter(
      (item) => !(item.id === comment.id && item.comment_type === comment.comment_type)
    )
  })
}

onMounted(loadComments)
</script>
